import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaGlobe,
  FaEdit,
  FaSearch,
  FaFileAlt,
  FaBook,
  FaCalculator,
  FaBuilding,
  FaCrown,
  FaTag,
  FaBookOpen,
  FaQuestionCircle,
  FaShieldAlt,
  FaCalendarAlt,
  FaIndustry,
  FaHome,
  FaUndo,
  FaDatabase,
  FaBars,
} from 'react-icons/fa';
import axios from 'axios';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';

const pagesData = [
  {
    id: 'home',
    title: 'Home Page',
    description: 'Edit hero, service cards, testimonials, approach highlights, FAQs, icons, and images. Preloaded defaults available.',
    path: '/cms/pages/home',
    icon: FaHome,
    color: 'from-blue-600 to-indigo-700',
    category: 'Core'
  },
  {
    id: 'navigation',
    title: 'Header & Footer',
    description: 'Edit logo, menu links, footer columns, and social icons. Preloaded navigation defaults available.',
    path: '/cms/pages/navigation-settings',
    icon: FaBars,
    color: 'from-slate-700 to-slate-900',
    category: 'Core'
  },
  {
    id: 'why-bookscomply',
    title: 'Why BooksComply',
    description: 'Edit badge, title, feature cards, icons, and descriptions. Reload preloaded defaults anytime.',
    path: '/cms/pages/why-bookscomply',
    icon: FaGlobe,
    color: 'from-blue-500 to-indigo-600',
    category: 'General'
  },
  {
    id: 'usa-company-formation',
    title: 'USA Company Formation',
    description: 'Customize state filings fees, pricing tables, process timelines, and page FAQs.',
    path: '/cms/pages/usa-company-formation',
    icon: FaBuilding,
    color: 'from-orange-500 to-red-600',
    category: 'Services'
  },
  {
    id: 'tax-return',
    title: 'Tax Return Page',
    description: 'Manage corporate/individual tax returns headings, steps, and compliance checklists.',
    path: '/cms/pages/tax-return',
    icon: FaCalculator,
    color: 'from-emerald-500 to-teal-600',
    category: 'Services'
  },
  {
    id: 'bookkeeping',
    title: 'Bookkeeping Page',
    description: 'Control QuickBooks setups, bank feed integrations, pricing ranges, and page FAQs.',
    path: '/cms/pages/bookkeeping',
    icon: FaBook,
    color: 'from-purple-500 to-pink-600',
    category: 'Services'
  },
  {
    id: 'cfo-advisory',
    title: 'CFO Advisory Page',
    description: 'Update strategic financial plans, growth metrics, pricing packages, and page FAQs.',
    path: '/cms/pages/cfo-advisory',
    icon: FaCrown,
    color: 'from-amber-500 to-yellow-600',
    category: 'Services'
  },
  {
    id: 'zoho-advisory',
    title: 'Zoho Advisory Page',
    description: 'Edit Zoho Books setup, payroll compliance, inventory integration, and page FAQs.',
    path: '/cms/pages/zoho-advisory',
    icon: FaGlobe,
    color: 'from-cyan-500 to-blue-600',
    category: 'Services'
  },
  {
    id: 'pricing',
    title: 'Pricing Page',
    description: 'Configure default pricing plans, comparison matrix, consultation steps, and page FAQs.',
    path: '/cms/pages/pricing',
    icon: FaTag,
    color: 'from-red-500 to-rose-600',
    category: 'Core'
  },
  {
    id: 'blog-page',
    title: 'Blog Page Layout',
    description: 'Set up blog page hero banner headers, description text, and visual banners.',
    path: '/cms/pages/blog-page',
    icon: FaBookOpen,
    color: 'from-violet-500 to-fuchsia-600',
    category: 'General'
  },
  {
    id: 'help-center',
    title: 'Help Center Page',
    description: 'Edit support categories, knowledgebase questions, and help articles.',
    path: '/cms/pages/help-center',
    icon: FaQuestionCircle,
    color: 'from-sky-500 to-blue-600',
    category: 'Support'
  },
  {
    id: 'legal-pages',
    title: 'Legal Pages',
    description: 'Update legal agreements, privacy policies, terms of service, and refund options.',
    path: '/cms/pages/legal-pages',
    icon: FaShieldAlt,
    color: 'from-slate-600 to-slate-800',
    category: 'Support'
  },
  {
    id: 'zoho-event',
    title: 'Zoho Event Page',
    description: 'Edit upcoming webinars, event descriptions, schedules, and guest speakers.',
    path: '/cms/pages/zoho-event',
    icon: FaCalendarAlt,
    color: 'from-orange-500 to-amber-600',
    category: 'Events'
  },
  {
    id: 'industry-pages',
    title: 'Industry Pages',
    description: 'Customize vertical industries (e-commerce, startups, retail) landing pages.',
    path: '/cms/pages/industry-pages',
    icon: FaIndustry,
    color: 'from-green-500 to-emerald-600',
    category: 'General'
  }
];

const AllPagesCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [syncing, setSyncing] = useState(false);

  const handleSeedMissing = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/preload/seed-missing`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess(res.data.message || 'Missing pages seeded from preloaded content');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to seed missing content');
    } finally {
      setSyncing(false);
    }
  };

  const handleReloadAll = async () => {
    if (!window.confirm(
      'This will overwrite ALL website CMS content with preloaded defaults (text, icons, images, sections). Your custom edits will be lost. Continue?'
    )) return;
    setSyncing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/preload/reload-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess(res.data.message || 'All preloaded content restored');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reload all content');
    } finally {
      setSyncing(false);
    }
  };

  const categories = ['All', 'General', 'Services', 'Core', 'Support', 'Events'];

  const filteredPages = pagesData.filter(page => {
    const matchesSearch = page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          page.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || page.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <PageLayout>
      <PageHeader
        title="Website Pages CMS"
        description="All pages ship with preloaded text, icons, images, and vectors. Select any page to edit, update, or remove content — or restore defaults per page."
        icon={FaGlobe}
      />

      <div className="bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/20 rounded-2xl border border-primary-100 dark:border-primary-800/40 p-6 space-y-4">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-primary-600 text-white shadow-lg shadow-primary-500/20">
            <FaDatabase className="w-5 h-5" />
          </div>
          <div className="flex-1 space-y-2">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Preloaded Content</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              Every main website page has preloaded content in the CMS — headings, body text, icons, images, vectors, FAQs, and sections.
              Admins can edit any field, add or remove items, upload new images, and use &quot;Reload Defaults&quot; on individual pages to restore the original preload.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={handleSeedMissing}
                disabled={syncing}
                className="px-4 py-2 rounded-xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors flex items-center gap-2"
              >
                <FaDatabase className="w-3.5 h-3.5" />
                {syncing ? 'Working...' : 'Seed Missing Pages'}
              </button>
              <button
                onClick={handleReloadAll}
                disabled={syncing}
                className="px-4 py-2 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 text-sm font-semibold text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors flex items-center gap-2"
              >
                <FaUndo className="w-3.5 h-3.5" />
                Reload All Defaults
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar & Filters */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            className="input-modern pl-11 py-2 text-sm"
            placeholder="Search pages by name or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Categories tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-hide">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-200 ${
                selectedCategory === cat
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/20'
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Pages Grid */}
      {filteredPages.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPages.map(page => {
            const Icon = page.icon;
            return (
              <div
                key={page.id}
                onClick={() => navigate(page.path)}
                className="group relative bg-white dark:bg-dark-800 rounded-3xl border border-gray-100 dark:border-dark-700/60 p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden"
              >
                {/* Colored highlight bubble */}
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${page.color} opacity-10 rounded-bl-full pointer-events-none group-hover:scale-110 transition-transform duration-300`} />
                
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3.5 bg-gradient-to-br ${page.color} rounded-2xl shadow-lg shadow-indigo-500/10`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="px-2.5 py-1 bg-gray-50 dark:bg-dark-750 text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 rounded-full tracking-wider">
                    {page.category}
                  </span>
                </div>

                {/* Body */}
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {page.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-6 min-h-[36px]">
                  {page.description}
                </p>

                {/* Footer action */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-50 dark:border-dark-700/50">
                  <span className="text-[10px] font-bold text-gray-400 uppercase">Status: Published</span>
                  <span className="flex items-center gap-1.5 text-xs font-bold text-primary-600 dark:text-primary-400 group-hover:translate-x-1 transition-transform duration-200">
                    Edit Page <FaEdit className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-800 rounded-3xl border border-gray-100 dark:border-dark-700 p-12 text-center">
          <div className="max-w-md mx-auto space-y-4">
            <div className="w-16 h-16 bg-gray-50 dark:bg-dark-750 rounded-2xl flex items-center justify-center mx-auto text-gray-400">
              <FaFileAlt className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">No pages match your search</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Try adjusting your query or filter tags to find the page you want to edit.
            </p>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default AllPagesCMS;
